import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, UserPlus } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

export default function DoctorsManagement() {
  const [doctors, setDoctors] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    specialization: '',
    license_number: '',
    phone: '',
    institution_id: '',
  });

  useEffect(() => {
    fetchDoctors();
    fetchInstitutions();
  }, []);

  const toggleStatus = async (doctor) => {
    const status = doctor.status === 'active' ? 'inactive' : 'active';
    try {
      await api.patch(`/doctors/${doctor.id}/status`, { status });
      toast.success(status === 'active' ? 'Médico aprobado' : 'Médico desactivado');
      fetchDoctors();
    } catch (error) {
      toast.error('No se pudo cambiar el estado');
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await api.get('/doctors');
      setDoctors(response.data);
    } catch (error) {
      toast.error('Error al cargar médicos');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutions = async () => {
    try {
      const response = await api.get('/institutions');
      setInstitutions(response.data);
    } catch (error) {
      console.error('Error fetching institutions');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/doctors', formData);
      toast.success('Médico creado exitosamente');
      setDialogOpen(false);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        specialization: '',
        license_number: '',
        phone: '',
        institution_id: '',
      });
      fetchDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear médico');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="doctors-title">
            Gestión de Médicos
          </h1>
          <p className="text-muted-foreground mt-2">Administra los médicos de la plataforma</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary" data-testid="create-doctor-button">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Médico
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="create-doctor-dialog">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Médico</DialogTitle>
              <DialogDescription>
                Complete los datos del nuevo médico en el sistema.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Nombre Completo *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    data-testid="doctor-full-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Correo Electrónico *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="doctor-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="doctor-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="doctor-phone-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="specialization">Especialización *</Label>
                  <Input
                    id="specialization"
                    value={formData.specialization}
                    onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                    required
                    data-testid="doctor-specialization-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="license_number">Número de Colegiatura *</Label>
                  <Input
                    id="license_number"
                    value={formData.license_number}
                    onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                    required
                    data-testid="doctor-license-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="institution_id">Institución</Label>
                <select
                  id="institution_id"
                  value={formData.institution_id}
                  onChange={(e) => setFormData({ ...formData, institution_id: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20"
                  data-testid="doctor-institution-select"
                >
                  <option value="">Seleccionar institución...</option>
                  {institutions.map((inst) => (
                    <option key={inst.id} value={inst.id}>
                      {inst.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-doctor-button">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-primary" data-testid="submit-doctor-button">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Crear Médico
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-6" data-testid="doctors-table-card">
        {loading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : doctors.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No hay médicos registrados
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Especialización</TableHead>
                <TableHead>Colegiatura</TableHead>
                <TableHead>Entidad declarada</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {doctors.map((doctor) => (
                <TableRow key={doctor.id} data-testid={`doctor-row-${doctor.id}`}>
                  <TableCell className="font-medium">{doctor.full_name}</TableCell>
                  <TableCell>{doctor.email}</TableCell>
                  <TableCell>{doctor.specialization}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {doctor.license_number}
                    </code>
                  </TableCell>
                  <TableCell className="text-xs">{doctor.requested_entity || '—'}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        doctor.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {doctor.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-2"
                      onClick={() => toggleStatus(doctor)}
                    >
                      {doctor.status === 'active' ? 'Desactivar' : 'Aprobar'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
